// React and Next.js imports
import type { GetStaticProps } from "next";
import { BuilderComponent, builder } from "@builder.io/react";
import React from "react";
import type { BasePageProps } from "@orlandohealth/build-kit";
import {
  SectionPage,
  ArticleWrapper,
  mergeTaxonomyTree,
  mergedTreeToBuildKitTaxonomies,
} from "@orlandohealth/build-kit";
import { slugify } from "@orlandohealth/utils";

// Define extended props interface to include regionalCareSite and articles
interface ContentHubPageProps extends BasePageProps {
  taxonomies: { name: string; slug: string; taxonomies?: any[] }[];
  builderLocale?: string;
}

export const getStaticProps: GetStaticProps<ContentHubPageProps> = async ({
  params: _params,
  locale,
  defaultLocale,
}) => {
  // Store the current API key
  const originalApiKey = builder.apiKey;

  // 1. First, use the dch API key for the page content
  builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_API_KEY || "";

  // Get the page content directly from Builder.io
  const page = await builder
    .get("page", {
      userAttributes: {
        urlPath: "/content-hub",
      },
      locale: locale || defaultLocale,
    })
    .promise();

  // Get base props for the page content
  const baseProps = await SectionPage.getStaticProps(
    "/content-hub",
    "content-hub",
    locale,
    defaultLocale,
    "page"
  );

  // Get base props (includes siteProperties)
  const siteProperties = await builder
    .get("site-properties", {
      locale: locale || "en",
      query: { name: process.env.NEXT_PUBLIC_REGIONAL_CARE_SITE || "dch" },
      options: {
        includeRefs: true,
        vercelPreview: true,
        locale: locale || "en",
        enrich: true,
      },
    })
    .promise();
  builder.apiKey = process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "";
  // Fetch taxonomy WITHOUT locale so name fields stay as Builder LocalizedValue
  // objects; mergeTaxonomyTree resolves localized display names while preserving
  // Default-locale `defaultName` values for slug generation.
  let taxonomyData = null;
  if (page?.data?.taxonomy?.referenceId) {
    taxonomyData = await builder.get("taxonomy", {
      options: { includeRefs: true },
      query: { id: page?.data?.taxonomy?.referenceId },
      apiKey: process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "",
    }).promise();
  } else if (siteProperties?.data?.taxonomies?.id) {
    taxonomyData = await builder.get("taxonomy", {
      options: { includeRefs: true },
      query: { id: siteProperties.data.taxonomies.id },
      apiKey: process.env.NEXT_PUBLIC_BUILDER_CORP_API_KEY || "",
    }).promise();
  } else if (siteProperties?.data?.taxonomies?.taxonomies) {
    taxonomyData = { data: siteProperties.taxonomies };
  } else {
    taxonomyData = await builder.get("taxonomy", {
      options: { includeRefs: true },
      query: { name: "content-hub" },
    }).promise();
  }

  // Restore original API key
  builder.apiKey = originalApiKey;

  const merged = mergeTaxonomyTree(taxonomyData?.data?.taxonomies, locale || defaultLocale || "en_US");
  const taxonomies = mergedTreeToBuildKitTaxonomies(merged, slugify);

  return {
    ...baseProps,
    props: {
      ...baseProps.props,
      content: page,
      taxonomies,
    },
  };
};

const ContentHubPage: React.FC<ContentHubPageProps> = (props) => {
  return (
    <ArticleWrapper {...props} url="/content-hub" taxonomies={props.taxonomies}>
      <BuilderComponent
        model="page"
        content={props.content}
        locale={props.builderLocale || "en"}
      />
    </ArticleWrapper>
  );
};

export default ContentHubPage;
